/**
 * <countdown-timer> - counts down to a REAL deadline that is the same for every visitor.
 *
 * Modes (set in the theme editor):
 *   end_date  fixed end date + time, e.g. the end of a campaign
 *   weekly    next occurrence of a weekday + time (e.g. every Sunday 23:59)
 *   daily     next occurrence of a time (e.g. an order cut-off at 23:59)
 *
 * Deadlines are interpreted in the store's time zone (data-timezone, Europe/Amsterdam), so a
 * visitor in another time zone sees the same moment. No cookies or per-visitor state: a timer that
 * restarts per visitor would state a deadline that does not exist.
 */
(function () {
  const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const formatters = {};

  /** Wall-clock parts of an instant in a time zone. weekday: 1 = Monday ... 7 = Sunday */
  function zonedParts(epochMs, timeZone) {
    formatters[timeZone] ||= new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    const parts = {};
    for (const { type, value } of formatters[timeZone].formatToParts(new Date(epochMs))) parts[type] = value;
    return {
      year: +parts.year,
      month: +parts.month,
      day: +parts.day,
      hour: +parts.hour,
      minute: +parts.minute,
      second: +parts.second,
      weekday: WEEKDAYS.indexOf(parts.weekday) + 1,
    };
  }

  /** Instant (epoch ms) of a wall-clock time in a time zone - correct across DST changes. */
  function zonedTimeToEpoch(year, month, day, hour, minute, timeZone) {
    const wall = Date.UTC(year, month - 1, day, hour, minute);
    let guess = wall;
    for (let i = 0; i < 3; i++) {
      const p = zonedParts(guess, timeZone);
      const offset = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - guess;
      const next = wall - offset;
      if (next === guess) break;
      guess = next;
    }
    return guess;
  }

  /** Calendar date `days` after y-m-d (handles month/year roll-over). */
  function addDays(year, month, day, days) {
    const d = new Date(Date.UTC(year, month - 1, day + days));
    return [d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()];
  }

  function parseTime(value) {
    const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec((value || '').trim());
    return m ? [+m[1], +m[2]] : null;
  }

  function parseDate(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
    if (!m) return null;
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    const check = new Date(Date.UTC(y, mo - 1, d));
    return check.getUTCMonth() === mo - 1 && check.getUTCDate() === d ? [y, mo, d] : null;
  }

  /**
   * Next deadline (epoch ms) for the settings, or null when the settings are invalid.
   * For end_date the deadline may lie in the past (= expired).
   */
  function computeDeadline({ mode, endDate, time, weekday, timeZone }, now = Date.now()) {
    const hm = parseTime(time);
    if (!hm) return null;
    const [hour, minute] = hm;

    if (mode === 'end_date') {
      const date = parseDate(endDate);
      return date ? zonedTimeToEpoch(date[0], date[1], date[2], hour, minute, timeZone) : null;
    }

    const today = zonedParts(now, timeZone);
    let offsetDays = 0;
    if (mode === 'weekly') {
      const target = +weekday;
      if (!(target >= 1 && target <= 7)) return null;
      offsetDays = (target - today.weekday + 7) % 7;
    } else if (mode !== 'daily') {
      return null;
    }

    let [y, mo, d] = addDays(today.year, today.month, today.day, offsetDays);
    let deadline = zonedTimeToEpoch(y, mo, d, hour, minute, timeZone);
    if (deadline <= now) {
      [y, mo, d] = addDays(y, mo, d, mode === 'weekly' ? 7 : 1);
      deadline = zonedTimeToEpoch(y, mo, d, hour, minute, timeZone);
    }
    return deadline;
  }

  /** Remaining time split into whole units. */
  function splitDuration(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    return {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };
  }

  const api = { zonedParts, zonedTimeToEpoch, computeDeadline, splitDuration, parseDate, parseTime };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api; // unit tests (Node)
    return;
  }

  const pad = (n) => String(n).padStart(2, '0');

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.settings = {
        mode: this.dataset.mode,
        endDate: this.dataset.endDate,
        time: this.dataset.time,
        weekday: this.dataset.weekday,
        timeZone: this.dataset.timezone || 'Europe/Amsterdam',
      };
      this.parts = {};
      for (const el of this.querySelectorAll('[data-part]')) this.parts[el.dataset.part] = el;
      this.label = this.querySelector('[data-label]');
      this.lastLabelMinute = null;

      this.deadline = computeDeadline(this.settings);
      if (this.deadline === null) return; // invalid settings: stay hidden
      this.tick();
    }

    disconnectedCallback() {
      clearTimeout(this.timeout);
    }

    tick() {
      const now = Date.now();
      let remaining = this.deadline - now;

      if (remaining <= 0) {
        if (this.settings.mode === 'end_date') return this.expire();
        this.deadline = computeDeadline(this.settings, now + 1000); // recurring: move to the next real deadline
        remaining = this.deadline - now;
      }

      const t = splitDuration(remaining);
      for (const unit of ['days', 'hours', 'minutes', 'seconds']) {
        if (this.parts[unit]) this.setDigits(this.parts[unit], pad(t[unit]));
      }
      this.updateLabel(t);
      this.hidden = false;

      this.timeout = setTimeout(() => this.tick(), 1000 - (Date.now() % 1000) + 20);
    }

    /** One fixed-width slot per digit; adds a slot when days exceed 99. */
    setDigits(el, text) {
      if (el.dataset.value === text) return;
      el.dataset.value = text;
      if (el.children.length !== text.length) {
        el.replaceChildren(
          ...Array.from(text, () => Object.assign(document.createElement('span'), { className: 'countdown-timer__digit' }))
        );
      }
      Array.from(text).forEach((ch, i) => {
        if (el.children[i].textContent !== ch) el.children[i].textContent = ch;
      });
    }

    /** Screen-reader text, updated once a minute (the digits themselves are aria-hidden). */
    updateLabel(t) {
      if (!this.label) return;
      const minuteKey = `${t.days}:${t.hours}:${t.minutes}`;
      if (minuteKey === this.lastLabelMinute) return;
      this.lastLabelMinute = minuteKey;
      const d = this.dataset;
      const unit = (n, one, many) => `${n} ${n === 1 ? one : many}`;
      const pieces = [];
      if (t.days) pieces.push(unit(t.days, d.labelDay, d.labelDays));
      if (t.days || t.hours) pieces.push(unit(t.hours, d.labelHour, d.labelHours));
      pieces.push(unit(t.minutes, d.labelMinute, d.labelMinutes));
      const joined = pieces.length > 1 ? `${pieces.slice(0, -1).join(', ')} ${d.labelAnd} ${pieces.at(-1)}` : pieces[0];
      this.label.textContent = `${d.labelPrefix} ${joined}`;
    }

    expire() {
      if (this.dataset.expired === 'hide_bar') {
        const section = this.closest('.shopify-section');
        if (section && !window.Shopify?.designMode) section.hidden = true;
      }
      this.hidden = true;
    }
  }

  if (!customElements.get('countdown-timer')) customElements.define('countdown-timer', CountdownTimer);
})();
